import React, { useState } from 'react';
import { Button, Spinner, Dropdown } from 'react-bootstrap'; // ✅ added Dropdown
import { useTranslation } from 'react-i18next';
import { useEyeTestExportMutation } from './eyetestApi';
import * as XLSX from 'xlsx';

const ExportExcel = ({ status, startDate, endDate }) => {
    const [buttonLoading, setButtonLoading] = useState(false);
    const [monthFilter, setMonthFilter] = useState(null); // ✅ NEW

    const { t } = useTranslation();
    const [exportEyeTest] = useEyeTestExportMutation();

    // ✅ helper for date format
    const formatDate = (date) => date.toISOString().split('T')[0];

    const exportHandle = async () => {
        setButtonLoading(true);

        try {
            // ✅ Status mapping
            const statusMapping = {
                approved: 'Approved',
                notApproved: 'Not Approved',
                pendingValidation: 'New',
                unassigned: 'New',
                assigned: 'New',
                new: 'New',
            };

            let queryParams = [];

            // ✅ Status
            if (status) {
                queryParams.push(`status=${statusMapping[status] || status}`);

                if (['unassigned', 'assigned', 'pendingValidation'].includes(status)) {
                    const assigUnassignedValue = status === 'unassigned' ? '0' : '1';
                    queryParams.push(`AssigUnassigned=${assigUnassignedValue}`);
                }
            }

            // ✅ 👉 NEW: override dates based on dropdown
            let finalStartDate = startDate;
            let finalEndDate = endDate;

            const today = new Date();

            if (monthFilter === 'current') {
                const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
                finalStartDate = formatDate(firstDay);
                finalEndDate = formatDate(today);
            }

            if (monthFilter === 'previous') {
                const firstDayPrev = new Date(today.getFullYear(), today.getMonth() - 1, 1);
                const lastDayPrev = new Date(today.getFullYear(), today.getMonth(), 0);

                finalStartDate = formatDate(firstDayPrev);
                finalEndDate = formatDate(lastDayPrev);
            }

            // ✅ Dates (use final values)
            if (finalStartDate) {
                queryParams.push(`startDate=${finalStartDate}`);
            }

            if (finalEndDate) {
                queryParams.push(`endDate=${finalEndDate}`);
            }

            const config = queryParams.join('&');

            const result = await exportEyeTest(config);

            let data = [];

            if (Array.isArray(result?.data)) {
                data = result.data;
            } else if (Array.isArray(result?.data?.data)) {
                data = result.data.data;
            } else if (Array.isArray(result)) {
                data = result;
            }

            if (!data.length) {
                alert("No data available for export");
                setButtonLoading(false);
                return;
            }

            const worksheet = XLSX.utils.json_to_sheet(data);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, 'Eyetest');

            const excelBuffer = XLSX.write(workbook, {
                bookType: 'xlsx',
                type: 'array',
            });

            const blob = new Blob([excelBuffer], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            });

            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;

            let filename = 'eyetest_data';

            if (finalStartDate && finalEndDate) {
                filename += `_${finalStartDate}_to_${finalEndDate}`;
            }

            link.setAttribute('download', `${filename}.xlsx`);

            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            URL.revokeObjectURL(url);

        } catch (error) {
            console.error('Export error:', error);
            alert("Something went wrong while exporting data");
        } finally {
            setButtonLoading(false);
        }
    };
    const isDisabledForExport =
        (!status || status === 'approved') && // Approved or All
        !monthFilter &&                      // no dropdown selected
        (!startDate || !endDate);
    return (
        <div className="d-flex align-items-center gap-2"> {/* ✅ wrapper added */}

            {/* ✅ NEW Dropdown */}
            <Dropdown onSelect={(val) => setMonthFilter(val)}>
                <Dropdown.Toggle variant="secondary" size="sm">
                    {monthFilter === 'current'
                        ? 'Current Month'
                        : monthFilter === 'previous'
                            ? 'Previous Month'
                            : 'Sélectionnez un mois'}
                </Dropdown.Toggle>

                <Dropdown.Menu>
                    <Dropdown.Item eventKey="current">
                        Mois en cours
                    </Dropdown.Item>
                    <Dropdown.Item eventKey="previous">
                        Mois précédent
                    </Dropdown.Item>
                </Dropdown.Menu>
            </Dropdown>

            {/* ✅ YOUR ORIGINAL BUTTON (UNCHANGED) */}
            <Button
                onClick={exportHandle}
                disabled={buttonLoading || isDisabledForExport}
                variant="primary"
                title={
                    isDisabledForExport
                        ? "Veuillez sélectionner une plage de dates ou un filtre par mois"
                        : ""
                }
                style={{
                    whiteSpace: "nowrap",
                    display: "inline-block",
                }}
                className="d-flex align-items-center gap-1 px-3"
            >
                {buttonLoading && (
                    <Spinner animation="border" role="status" size="sm">
                        <span className="visually-hidden">Loading...</span>
                    </Spinner>
                )}
                Exporter des données (Excel)
            </Button>
        </div>
    );
};

export default ExportExcel;